import React, { useState, useEffect, useRef } from 'react';
import { Address, LatLng } from '@core/generated/gql-types';
import Autocomplete from 'react-google-autocomplete';
import {
  buildAddressFromPlace,
  buildFriendlyAddressFromParts,
  generateFriendlyAddress,
} from '@core/util/google-places.service';

interface AddressFormProps {
  onAddressChange: (address: Address | null) => void;
  onCoordinatesChange?: (coordinates: LatLng | null) => void;
  initialAddress?: Address;
  required?: boolean;
  onManualEntryChange?: (isManual: boolean) => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  onAddressChange,
  onCoordinatesChange,
  initialAddress,
  required = true,
  onManualEntryChange,
}) => {
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(
    initialAddress || null,
  );
  const [searchValue, setSearchValue] = useState<string>(
    initialAddress ? generateFriendlyAddress(initialAddress) || '' : '',
  );

  // Track if a place was actually selected (not just typed)
  const hasSelectedPlaceRef = useRef(!!initialAddress?.addressLine1);

  const initialAddressKey = [
    initialAddress?.addressLine1,
    initialAddress?.addressLine2,
    initialAddress?.suburb,
    initialAddress?.state,
    initialAddress?.postcode,
    initialAddress?.countryLabel,
  ].join('|');

  const syncedInitialAddressKeyRef = useRef<string | null>(null);

  // Sync local display when the initial address identity changes (do not call
  // onAddressChange here — that creates a Formik ↔ AddressForm update loop).
  useEffect(() => {
    if (!initialAddress?.addressLine1) {
      return;
    }
    if (syncedInitialAddressKeyRef.current === initialAddressKey) {
      return;
    }
    syncedInitialAddressKeyRef.current = initialAddressKey;
    setSelectedAddress(initialAddress);
    setSearchValue(generateFriendlyAddress(initialAddress) || '');
    hasSelectedPlaceRef.current = true;
  }, [initialAddress, initialAddressKey]);

  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newAddress = {
      ...(selectedAddress || {
        addressLine1: '',
        addressLine2: '',
        suburb: '',
        state: '',
        postcode: '',
        countryLabel: '',
        countryValue: '',
      }),
      [name]: value,
      ...(name === 'countryLabel' && { countryValue: value }),
    };
    const addressWithFriendly = {
      ...newAddress,
      addressFriendly: buildFriendlyAddressFromParts(newAddress),
    };
    setSelectedAddress(addressWithFriendly);
    onAddressChange(addressWithFriendly);
    onCoordinatesChange?.(null);
  };

  const handleGooglePlaceSelect = (place: any) => {
    if (!place.address_components) return;

    const { address, latLng } = buildAddressFromPlace(place);

    setSelectedAddress(address);
    setSearchValue(
      address.addressFriendly || generateFriendlyAddress(address) || '',
    );
    hasSelectedPlaceRef.current = true;
    onAddressChange(address);
    onCoordinatesChange?.(latLng);
  };

  const handleAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const friendlySelectedAddress = generateFriendlyAddress(
      selectedAddress || {},
    );

    // Ignore spurious change events while the existing selection is still displayed.
    if (
      hasSelectedPlaceRef.current &&
      selectedAddress?.addressLine1 &&
      (inputValue === friendlySelectedAddress ||
        inputValue === selectedAddress.addressFriendly)
    ) {
      return;
    }

    if (inputValue === searchValue) {
      return;
    }

    setSearchValue(inputValue);

    // If user types after selecting a place, clear the selection
    if (
      hasSelectedPlaceRef.current &&
      inputValue !== friendlySelectedAddress &&
      inputValue !== selectedAddress?.addressFriendly
    ) {
      setSelectedAddress(null);
      hasSelectedPlaceRef.current = false;
      onAddressChange(null);
      onCoordinatesChange?.(null);
    } else if (!hasSelectedPlaceRef.current && inputValue) {
      // User is typing but hasn't selected a place - mark as invalid
      onAddressChange(null);
      onCoordinatesChange?.(null);
    } else if (!inputValue) {
      // Input is cleared - no address
      setSelectedAddress(null);
      hasSelectedPlaceRef.current = false;
      onAddressChange(null);
      onCoordinatesChange?.(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-800">
          Address Information {required && '*'}
        </h4>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Can't find the address?</span>
          <button
            type="button"
            onClick={() => {
              const newValue = !isManualEntry;
              setIsManualEntry(newValue);
              onManualEntryChange?.(newValue);
              if (newValue) {
                onCoordinatesChange?.(null);
                if (selectedAddress) {
                  const addressWithFriendly = {
                    ...selectedAddress,
                    addressFriendly:
                      buildFriendlyAddressFromParts(selectedAddress),
                  };
                  setSelectedAddress(addressWithFriendly);
                  onAddressChange(addressWithFriendly);
                }
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden ${
              isManualEntry ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isManualEntry ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {isManualEntry ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="addressLine1"
            >
              Address Line 1 {required && '*'}
            </label>
            <input
              type="text"
              id="addressLine1"
              name="addressLine1"
              value={selectedAddress?.addressLine1 || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              required={required}
            />
          </div>

          <div className="col-span-2">
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="addressLine2"
            >
              Address Line 2
            </label>
            <input
              type="text"
              id="addressLine2"
              name="addressLine2"
              value={selectedAddress?.addressLine2 || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="suburb"
            >
              Suburb {required && '*'}
            </label>
            <input
              type="text"
              id="suburb"
              name="suburb"
              value={selectedAddress?.suburb || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              required={required}
            />
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="state"
            >
              State {required && '*'}
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={selectedAddress?.state || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              required={required}
            />
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="postcode"
            >
              Postcode {required && '*'}
            </label>
            <input
              type="text"
              id="postcode"
              name="postcode"
              value={selectedAddress?.postcode || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              required={required}
            />
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="countryLabel"
            >
              Country {required && '*'}
            </label>
            <input
              type="text"
              id="countryLabel"
              name="countryLabel"
              value={selectedAddress?.countryLabel || ''}
              onChange={handleManualInputChange}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              required={required}
            />
          </div>
        </div>
      ) : (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Search Address {required && '*'}
          </label>
          <Autocomplete
            apiKey={import.meta.env.VITE_GOOGLE_PLACES_API_KEY}
            onPlaceSelected={handleGooglePlaceSelect}
            onChange={handleAutocompleteChange}
            options={{
              types: ['geocode', 'establishment'],
              componentRestrictions: { country: 'au' },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // When this is rendered inside a form, pressing enter will submit the form.
                // We don't want that. Disable it.
                e.preventDefault();
              }
            }}
            value={searchValue}
            className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Search for an address..."
          />
        </div>
      )}
    </div>
  );
};

export default AddressForm;
